import React, { useEffect } from 'react';

import { useFilters } from '@/utils';

import { useFavoriteReportHandler } from '../../hooks';
import { ReportDetailsProps, ReportTab, ReportTypeKeys } from '../../report.types';
import { useGetReportEmployerId } from '../hooks';
import ReportDetailsBase from '../ReportDetailsBase';

import {
  CardStatusReportTableParams,
  CardStatusSummaryReportParams,
  LAST_30_DAYS_KEY,
} from './CardStatusReport.types';
import CardStatusReportSummary from './CardStatusReportSummary';
import CardStatusReportTable from './CardStatusReportTable';

type FiltersValues = CardStatusReportTableParams &
  CardStatusSummaryReportParams & {
    tab: ReportTab;
  };

interface Props extends ReportDetailsProps<FiltersValues> {}
const CardStatusReportDetails: React.FC<Props> = ({
  defaultEmployerId,
  defaultEmployerIds,
  defaultTab,
  isPreviewMode,
  onChangeFilters,
  defaultFilterValues,
}) => {
  const employerId = useGetReportEmployerId();
  const { filtersValues, setFilterValue } = useFilters<FiltersValues>(
    {
      tab: defaultTab || ReportTab.Summary,
      employerId: defaultEmployerId || employerId || '',
      employerIds: defaultEmployerIds || employerId ? [employerId] : [],
      searchString: '',
      isSSNShowed: false,
      cardStatuses: [],
      employmentStatuses: [],
      mailedStartDate: null,
      mailedEndDate: null,
      cardStatusGroupType: LAST_30_DAYS_KEY,
      ...defaultFilterValues,
    },
    {
      disabled: isPreviewMode,
      autoClear: true,
    }
  );
  useEffect(() => {
    setFilterValue.cardStatuses([]);
    setFilterValue.employmentStatuses([]);
    setFilterValue.mailedStartDate(null);
    setFilterValue.mailedEndDate(null);
    setFilterValue.cardStatusGroupType(LAST_30_DAYS_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId, filtersValues?.employerIds?.join('')]);
  useEffect(() => {
    if (employerId && !filtersValues.employerId) {
      setFilterValue.employerId(employerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);
  useEffect(() => {
    if (defaultTab) {
      setFilterValue.tab(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);
  useEffect(() => {
    if (isPreviewMode && onChangeFilters) {
      onChangeFilters(filtersValues);
    }
  }, [filtersValues, isPreviewMode, onChangeFilters]);
  const { isFavorite, handleToggleReportKey } = useFavoriteReportHandler(ReportTypeKeys.CardStatus);
  return (
    <ReportDetailsBase
      isPreviewMode={isPreviewMode}
      isFavorite={isFavorite}
      onToggleFavorite={handleToggleReportKey}
      tab={filtersValues.tab}
      onSetTab={setFilterValue.tab}
      title="Card Status"
    >
      {filtersValues.tab === ReportTab.Report ? (
        <CardStatusReportTable
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          key={filtersValues.tab}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      ) : (
        <CardStatusReportSummary
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      )}
    </ReportDetailsBase>
  );
};

export default CardStatusReportDetails;
